import React, { useEffect, useMemo, useState } from 'react'
import {
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonList,
  IonNavLink,
  IonRange,
  IonRow,
  IonTextarea,
  IonToggle,
} from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'

import { CustomVitalConstants, VitalsConstants } from '../store/vitals.constants'
import { IRootState } from '../../../store'
import { useTranslation } from 'react-i18next'

import '../page/Vitals.page.css'
import textAreaStyles from '../../../components/textArea/TextArea.component.module.css'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import {
  saveVitals,
  setComments,
  setEtCO2Type,
  setLevelOfConsciousness,
  setPain,
  setPainScales,
  setPainPNNV,
  setProtocol,
  setRts,
  setStrokeScale,
  setStrokeScalePNNV,
  setTemperature,
  setTemperatureMethod,
  setTemperatureType,
  setIsPainRangeActivated,
  setPainType,
  setEtCO2PNNV,
  setTemperaturePNNV,
  setSpCOPNNV,
  setCustomVitalLabel1,
  setCustomVitalValue2,
  setCustomVitalValue1,
  setCustomVitalLabel2,
  showAlert,
  setPainQuality,
} from '../store/vitals.actions'
import { numberPadKeysWithDecimal } from '../../../components/numberPad/NumberPad.types'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import GlasgowComaScaleComponent from '../GlasgowComaScaleSection/GlasgowComaScale.component'
import Validation from '../../../components/validation/Validation.component'
import { getAllPNNVOptionsSelector, getVitalOptionsSelector } from '../../options/selectors'
import { IOption, IPNNVOption } from '../../../common/common.types'
import { getStyleForPHI, IPHIComponent } from '../../../components/phi/phi.component'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import { getDefaultTempTypeSelector } from '../../configurations/selectors/defaultTempType.selector'
// import StrokeScaleSection from '../strokeScaleSection/strokeScaleSection.component'
import GlucoseSelectionComponent from './GlucoseSelection.component'
import { ITripInformationState } from '../../tripInformation/store/tripInformation.types'
import { getTripInformationSelector } from '../../tripInformation/selectors'
import { IConfigurationsState } from '../../configurations/store/configurations.types'
import { getConfigurationsSelector } from '../../configurations/selectors'
import { closeCircle } from 'ionicons/icons'
import { numericPainScaleType } from '../store/vitals.reducers'
import ApgarSection from '../apgarSection/ApgarSection.component'
import { getPatientDemographicsSelector } from '../../patientDemographics/selectors'
import { calculateAge, isValidNemsisDOB } from '../../../utilities/dateTime.util'
import SInput from '../../../components/stringInput/StringInput.component'
import { getPatientTimelineSelector } from '../../timeline/selectors/patientTimeline.selector'
import ScoreButton from '../../../components/scoreButton/ScoreButton.component'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import {
  getAllowedStrokeScoreIds,
  IStrokeScaleConfigOption,
} from '../../initialAssessments/neuro/strokeScore/utils/strokeScaleScore.util'
import { useScrollRestoration } from '../../../utilities/useScrollRestoration'

const AdvancedSection: React.FC<IPHIComponent> = (props: IPHIComponent) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { isPHI } = props
  const history = useHistory()
  useScrollRestoration()

  const tripInformationState: ITripInformationState = useSelector(getTripInformationSelector)
  const { staffingLevel } = tripInformationState

  const vitals = useSelector((state: IRootState) => state.vitals)
  const {
    temperature,
    temperaturePNNV,
    temperatureType,
    temperatureMethod,
    spCO,
    spCOPNNV,
    spMet,
    rts,
    etCO2,
    etCO2PNNV,
    glucose,
    levelOfConsciousness,
    strokeScale,
    strokeScalePNNV,
    strokeScaleScoreTotal,
    strokeScaleScoreTotalPNNV,
    // strokeScoreFlag,
    pain,
    painPNNV,
    painScales,
    sedationScale,
    protocol,
    comments,
    reRunValidations,
    etCO2Type,
    __isPainRangeActivated,
    key,
    customVitalLabel1,
    customVitalLabel2,
    customVitalValue1,
    customVitalValue2,
    __customVitalLabelExists,
    withdrawalScaleCriteria,
    withdrawalScaleTotal,
    withdrawalScaleTotalPNNV,
  } = vitals
  const vitalOptions = useSelector(getVitalOptionsSelector)
  const {
    protocolOptions,
    neuroLocOptions,
    temperatureMethodOptions,
    strokeScaleOptions,
    etco2TypeOptions,
    strokeScaleConfigOptions,
  } = vitalOptions
  const allowedScoreIds = getAllowedStrokeScoreIds(
    strokeScale?.id,
    strokeScaleConfigOptions as IStrokeScaleConfigOption[]
  )
  const defaultTempType = useSelector(getDefaultTempTypeSelector)
  const configuration: IConfigurationsState = useSelector(getConfigurationsSelector)
  const { datapointsConfig } = configuration
  const [isFocused, setIsFocused] = useState(false)
  const [continueEditing, setContinueEditing] = useState({
    label1: false,
    label2: false,
  })

  const [showConfirmation, setShowConfirmation] = useState({
    label1: false,
    label2: false,
  })

  const [tempCustomVitalLabel, setTempCustomVitalLabel] = useState({
    label1: '',
    label2: '',
  })

  const { isTablet: isTabletDevice } = useDeviceMetrics()
  const {
    DP_CUSTOMVITALS,
    DP_OPT1_NAME: fixedLabel1,
    DP_OPT2_NAME: fixedLabel2,
    DP_HIDE_FIELDS_BY_STAFF,
    DP_PAIN_QUALITY,
    DP_PAIN,
    DP_SEDATION,
    DP_WITHDRAWAL,
  } = datapointsConfig

  const allPNNVOptions = useSelector(getAllPNNVOptionsSelector)
  const etCO2PNNVOptions = allPNNVOptions?.['eVitals.16']
  const spCOPNNVOptions = allPNNVOptions?.['eVitals.17']
  const temperaturePNNVOptions = allPNNVOptions?.['eVitals.24']
  const painPNNVOptions = allPNNVOptions?.['eVitals.27']

  const showETC02 = DP_HIDE_FIELDS_BY_STAFF === 0 || (staffingLevel && staffingLevel.isAls)

  const patientDemographics = useSelector(getPatientDemographicsSelector)
  const { dOB, age, ageUnit } = patientDemographics

  const { filteredItems } = useSelector(getPatientTimelineSelector)
  const timelineVitals = filteredItems?.filter((item) => item.itemType === 'Vitals' && item.key !== key)
  const moreThanOneVitalsInTimeline = timelineVitals && timelineVitals.length > 0

  const isApgarSectionVisible = () => {
    if (age && ageUnit && ageUnit.id) {
      if (ageUnit.id.toString() === 'y') return +age === 0
      if (ageUnit.id.toString() === 'm') return +age === 0
      if (ageUnit.id.toString() === 'd') return +age <= 1
      if (ageUnit.id.toString() === 'h') return +age <= 24
    }

    if (dOB && isValidNemsisDOB(dOB)) {
      const ageCalculation = calculateAge(dOB)
      if (ageCalculation.years === 0 && ageCalculation.months === 0 && ageCalculation.days <= 1) return true
    }

    return false
  }

  useEffect(() => {
    if (!temperatureType) {
      dispatch(setTemperatureType(defaultTempType?.toUpperCase() || 'F'))
    }
  })

  const onFocus = () => {
    setIsFocused(true)
  }

  const onBlur = () => {
    setIsFocused(false)
  }

  const setTemperatureOnChange = (numbers: string[]) => {
    dispatch({
      type: VitalsConstants.SET_TEMPERATURE,
      temperature: numbers.length ? numbers.join('') : null,
      conditionalValue: temperatureType,
    })
    dispatch(saveVitals())
  }

  const setSpCOOnChange = (numbers: string[]) => {
    dispatch({
      type: VitalsConstants.SET_SPCO,
      spCO: numbers.length ? numbers.join('') : null,
    })
    dispatch(saveVitals())
  }

  const setSpCOPNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setSpCOPNNV(option))
    dispatch(saveVitals())
  }

  const setSpMetOnChange = (numbers: string[]) => {
    dispatch({
      type: VitalsConstants.SET_SPMET,
      spMet: numbers.length ? numbers.join('') : null,
    })
    dispatch(saveVitals())
  }

  const setEtCO2OnChange = (numbers: string[]) => {
    dispatch({
      type: VitalsConstants.SET_ETCO2,
      etCO2: numbers.length ? numbers.join('') : null,
    })
    dispatch(saveVitals())
  }

  const setTemperatureTypeOnChange = () => {
    const tempType =
      temperatureType === t('vitals.advanced.form.F') ? t('vitals.advanced.form.C') : t('vitals.advanced.form.F')
    dispatch({
      type: VitalsConstants.SET_TEMPERATURE_TYPE,
      temperatureType: tempType,
    })
    dispatch(setTemperature(temperature, tempType))
    dispatch(saveVitals())
  }

  const setTemperaturePNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setTemperaturePNNV(option))
    dispatch(saveVitals())
  }

  const setTemperatureMethodOnChange = (option: IOption) => {
    dispatch(setTemperatureMethod(option))
    dispatch(saveVitals())
  }

  const setEtCO2TypeChange = (option: IOption) => {
    dispatch(setEtCO2Type(option))
    dispatch(saveVitals())
  }

  const setEtCO2PNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setEtCO2PNNV(option))
    dispatch(saveVitals())
  }

  const rtsOnChange = (numbers: string[]) => {
    dispatch(setRts(numbers.length ? numbers.join('') : null))
    dispatch(saveVitals())
  }

  const levelOfConsciousnessOnChange = (option: IOption) => {
    dispatch(setLevelOfConsciousness(option))
    dispatch(saveVitals())
  }

  const painOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setPain(e.detail.value))
    dispatch(setPainType(numericPainScaleType))
    if (e.detail.value !== pain && !(e.detail.value === 0 && pain === '')) dispatch(saveVitals())
  }

  const clearPainNormalValues = (pnnvOption: IPNNVOption | null) => {
    dispatch(setPainPNNV(pnnvOption))
    dispatch(setPainType(null))
    if (pain) dispatch(setPain(''))
    if (painQualityHasData) dispatch(setPainQuality(null))
    if (hasPainScale) dispatch(setPainScales(null))
    dispatch(saveVitals())
  }

  const painPNNVOnChange = (pnnvOption: IPNNVOption | null) => {
    if (pnnvOption && (painQualityHasData || hasPainScale)) {
      dispatch(
        showAlert({
          header: '',
          message: t('vitals.advanced.pnnvWarning'),
          buttons: [
            {
              text: t('common.buttons.Cancel'),
              handler: () => {
                return
              },
            },
            {
              text: t('common.buttons.Ok'),
              role: 'destructive',
              handler: () => clearPainNormalValues(pnnvOption),
            },
          ],
        })
      )
    } else {
      clearPainNormalValues(pnnvOption)
    }
  }

  const strokeScaleOnChange = (option: IOption) => {
    dispatch(setStrokeScale(option, option?.dataAttribute1))
    dispatch(saveVitals())
  }

  const strokeScalePNNVOnChange = (pnnvOption: IPNNVOption | null) => {
    dispatch(setStrokeScalePNNV(pnnvOption))
    dispatch(saveVitals())
  }

  const commentsOnChange = (e: CustomEvent) => {
    if (e.detail.value !== comments) {
      dispatch(setComments(e.detail.value))
      dispatch(saveVitals())
    }
  }

  const protocolOnChange = (option: IOption) => {
    dispatch(setProtocol(option))
    dispatch(saveVitals())
  }

  const handleDeletePainScale = (e: React.MouseEvent<HTMLIonIconElement, MouseEvent>) => {
    dispatch(setIsPainRangeActivated(false))
    dispatch(setPain(''))
    dispatch(setPainType(null))
    dispatch(saveVitals())
    e.stopPropagation()
  }

  useEffect(() => {
    temperature &&
      dispatch({
        type: VitalsConstants.SET_TEMPERATURE,
        temperature: temperature,
        conditionalValue: temperatureType,
      })

    etCO2 &&
      dispatch({
        type: VitalsConstants.SET_ETCO2,
        etCO2: etCO2,
      })

    spCO &&
      dispatch({
        type: VitalsConstants.SET_SPCO,
        spCO: spCO,
      })

    spMet &&
      dispatch({
        type: VitalsConstants.SET_SPMET,
        spMet: spMet,
      })

    glucose &&
      dispatch({
        type: VitalsConstants.SET_GLUCOSE,
        glucose: glucose,
      })

    comments && dispatch(setComments(comments))
  }, [reRunValidations])

  const normalizeInput = (value: string): string => {
    return value?.trim() === '' ? '' : value
  }

  const showCustomVitalEditAlert = <T extends (val: string) => object>({
    labelKey,
    value,
    setCustomVitalLabel,
  }: {
    labelKey: 'label1' | 'label2'
    value: string
    setCustomVitalLabel: T
  }) => {
    dispatch(
      showAlert({
        header: '',
        message: t('vitals.advanced.CustomLabelEditConfirmation'),
        buttons: [
          {
            text: t('common.buttons.Cancel'),
            handler: () => {
              setShowConfirmation((prev) => ({ ...prev, [labelKey]: false }))
              setTempCustomVitalLabel((prev) => ({ ...prev, [labelKey]: '' }))
            },
          },
          {
            text: t('common.buttons.Ok'),
            handler: () => {
              dispatch(setCustomVitalLabel(value))
              dispatch(saveVitals())
              setContinueEditing((prev) => ({ ...prev, [labelKey]: true }))
              setShowConfirmation((prev) => ({ ...prev, [labelKey]: false }))
              setTempCustomVitalLabel((prev) => ({ ...prev, [labelKey]: '' }))
            },
          },
        ],
      })
    )
  }

  const handleCustomVitalLabelChange = <T extends (val: string) => object>(
    labelKey: 'label1' | 'label2',
    setCustomVitalLabel: T
  ) => {
    return (e: CustomEvent) => {
      e.preventDefault()
      const value = normalizeInput(e.detail.value)

      const showAlert =
        moreThanOneVitalsInTimeline &&
        __customVitalLabelExists &&
        !continueEditing[labelKey] &&
        !showConfirmation[labelKey]

      if (showAlert) {
        setTempCustomVitalLabel((prev) => ({ ...prev, [labelKey]: value }))
        setShowConfirmation((prev) => ({ ...prev, [labelKey]: true }))
        showCustomVitalEditAlert({ labelKey, value, setCustomVitalLabel })
      } else {
        dispatch(setCustomVitalLabel(value))
        dispatch(saveVitals())
      }
    }
  }

  const setCustomVitalValue1OnChange = (e: CustomEvent) => {
    e.preventDefault()
    const value = normalizeInput(e.detail.value)
    dispatch(setCustomVitalValue1(value))
    dispatch(saveVitals())
  }

  const setCustomVitalValue2OnChange = (e: CustomEvent) => {
    e.preventDefault()
    const value = normalizeInput(e.detail.value)
    dispatch(setCustomVitalValue2(value))
    dispatch(saveVitals())
  }

  const handlePainQualityClick = () => {
    if (painPNNV) return
    history.push(TabConstants.VITALS_PAIN_QUALITY)
  }

  const setCustomVitalLabel1OnChange = handleCustomVitalLabelChange('label1', setCustomVitalLabel1)
  const setCustomVitalLabel2OnChange = handleCustomVitalLabelChange('label2', setCustomVitalLabel2)

  const editLabelMode = DP_CUSTOMVITALS === CustomVitalConstants.EDITABLE_LABEL_CUSTOM_VITALS
  const fixedLabelMode = DP_CUSTOMVITALS === CustomVitalConstants.FIXED_LABEL_CUSTOM_VITALS
  const showCustomVitals = DP_CUSTOMVITALS !== CustomVitalConstants.HIDE_CUSTOM_VITALS

  const painPNNVJSX = (
    <SingleSelect
      dataTestID={t('vitals.advanced.form.Pain')}
      options={painPNNVOptions}
      listHeader={t('vitals.advanced.form.Pain')}
      label={painPNNV ? t('vitals.advanced.form.Pain') : t('vitals.advanced.form.PainScalePNNV')}
      action={() => {
        //
      }}
      selectedPNNVOption={painPNNV}
      pnnvAction={painPNNVOnChange}
      showPNNVAsNormals={true}
    />
  )

  const painQualityHasData = useMemo(() => {
    return (
      !!vitals.painQuality &&
      ((vitals.painQuality.onset ?? '').trim() !== '' ||
        !!vitals.painQuality.onsetUnit ||
        !!vitals.painQuality.field1 ||
        !!vitals.painQuality.field2 ||
        !!vitals.painQuality.field3 ||
        !!vitals.painQuality.field4 ||
        !!vitals.painQuality.field5 ||
        !!vitals.painQuality.field6 ||
        !!vitals.painQuality.field7 ||
        (vitals.painQuality.comments ?? '').trim() !== '')
    )
  }, [vitals.painQuality])

  const hasPainScale = !!painScales?.painScaleSelections?.length
  const hasPainData = !!pain || !!painPNNV
  const hasWithdrawalScaleData = withdrawalScaleTotal || withdrawalScaleTotalPNNV || withdrawalScaleCriteria?.length

  const painScalesTotalsForButton = painScales?.totalsCombined || 'N/A'
  const sedationScaleTotal = sedationScale?.totalsCombined || 'N/A'
  const hasSedationScale = !!sedationScale?.selections?.length

  return (
    <IonList>
      <IonItemDivider color='light' className='ion-padding'>
        <IonLabel className='grid-item-divider-label' color='medium'>
          {t('vitals.advanced.header')}
        </IonLabel>
      </IonItemDivider>

      <IonGrid className='ion-no-padding'>
        <IonRow className='mobile-nowrap'>
          <IonCol className='picklist'>
            <NumberPad
              elementId='Temperature'
              dataTestId='Temperature'
              values={temperature ? temperature.split('') : []}
              label={t('vitals.advanced.form.Temperature')}
              validCharacters={numberPadKeysWithDecimal}
              onNumPadClick={setTemperatureOnChange}
              pnnvListHeader={t('vitals.advanced.form.Temperature')}
              pnnvAction={setTemperaturePNNVOnChange}
              pnnvValue={temperaturePNNV}
              pnnvOptions={temperaturePNNVOptions}
            />
            <Validation id={VitalsConstants.SET_TEMPERATURE} cardinalityKey={key} />
          </IonCol>

          <IonCol>
            <IonItem lines='none'>
              <IonLabel className='toggle-prefix-width'>{t('vitals.advanced.form.C')}°</IonLabel>
              <IonToggle
                disabled={temperaturePNNV ? true : false}
                data-testid='TemperatureType'
                className='temperature-ion-toggle'
                checked={!temperatureType || temperatureType === t('vitals.advanced.form.F')}
                onClick={() => {
                  setTemperatureTypeOnChange()
                }}
              />
              <IonLabel className='toggle-postfix-padding'>{t('vitals.advanced.form.F')}°</IonLabel>
            </IonItem>
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              disabled={temperaturePNNV ? true : false}
              dataTestID='TemperatureMethod'
              options={temperatureMethodOptions}
              selectedOption={temperatureMethod}
              listHeader={t('vitals.advanced.form.TemperatureMethod')}
              label={t('vitals.advanced.form.TemperatureMethod')}
              action={setTemperatureMethodOnChange}
            />
          </IonCol>
        </IonRow>
      </IonGrid>

      <IonItemDivider color='light' />

      <IonGrid className='ion-no-padding'>
        {showETC02 && (
          <IonRow>
            <IonCol className='picklist'>
              <NumberPad
                elementId='EtCO2'
                dataTestId='EtCO2'
                values={etCO2 ? etCO2.split('') : []}
                label={t('vitals.advanced.form.ETCO2')}
                onNumPadClick={setEtCO2OnChange}
                action={VitalsConstants.SET_ETCO2}
                validCharacters={numberPadKeysWithDecimal}
                pnnvListHeader={t('vitals.advanced.form.ETCO2')}
                pnnvAction={setEtCO2PNNVOnChange}
                pnnvValue={etCO2PNNV}
                pnnvOptions={etCO2PNNVOptions}
              />
              <Validation id={VitalsConstants.SET_ETCO2} cardinalityKey={key} />
            </IonCol>
            <IonCol className='picklist'>
              <SingleSelect
                disabled={etCO2PNNV ? true : false}
                dataTestID={t('vitals.advanced.form.etcO2Type')}
                options={etco2TypeOptions}
                selectedOption={etCO2Type}
                listHeader={t('vitals.advanced.form.etcO2Type')}
                label={t('vitals.advanced.form.etcO2Type')}
                action={setEtCO2TypeChange}
              />
            </IonCol>
          </IonRow>
        )}

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <NumberPad
              elementId='SpCO'
              dataTestId='SpCO'
              values={spCO ? spCO.split('') : []}
              label={t('vitals.advanced.form.spCO')}
              onNumPadClick={setSpCOOnChange}
              action={VitalsConstants.SET_SPCO}
              validCharacters={numberPadKeysWithDecimal}
              pnnvListHeader={t('vitals.advanced.form.spCO')}
              pnnvAction={setSpCOPNNVOnChange}
              pnnvValue={spCOPNNV}
              pnnvOptions={spCOPNNVOptions}
            />
            <Validation id={VitalsConstants.SET_SPCO} cardinalityKey={key} />
          </IonCol>
          <IonCol className='picklist'>
            <NumberPad
              elementId='SpMet'
              dataTestId='SpMet'
              values={spMet ? spMet.split('') : []}
              label={t('vitals.advanced.form.spMet')}
              onNumPadClick={setSpMetOnChange}
              action={VitalsConstants.SET_SPMET}
              validCharacters={numberPadKeysWithDecimal}
            />
            <Validation id={VitalsConstants.SET_SPMET} cardinalityKey={key} />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <NumberPad
              elementId='RTS'
              dataTestId='RTS'
              values={rts ? rts.split('') : []}
              label={t('vitals.advanced.form.RTS')}
              onNumPadClick={rtsOnChange}
              action={VitalsConstants.SET_ETCO2}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        <GlucoseSelectionComponent />
      </IonGrid>

      <IonItemDivider color='light' />

      <IonGrid className='ion-no-padding'>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='LevelOfConsciusnessOptions'
              options={neuroLocOptions}
              selectedOption={levelOfConsciousness}
              listHeader={t('vitals.advanced.form.LevelofConsciousness')}
              label={t('vitals.advanced.form.LevelofConsciousness')}
              action={levelOfConsciousnessOnChange}
            />
          </IonCol>
        </IonRow>

        {(!!DP_PAIN || hasPainScale || hasPainData) && (
          <>
            <hr className='field-divider' />
            {painPNNV && (
              <IonRow>
                <IonCol>{painPNNVJSX}</IonCol>
              </IonRow>
            )}
            {!painPNNV && (
              <IonRow className='pain-input-field-wrapper'>
                <IonCol sizeXs='5' className='picklist'>
                  <IonItem
                    onClick={() => {
                      dispatch(setIsPainRangeActivated(true))
                    }}
                    lines='none'>
                    <IonLabel color='medium'>{t('vitals.advanced.form.Pain')}</IonLabel>
                  </IonItem>
                </IonCol>

                {!__isPainRangeActivated && (
                  <IonCol className='range-selection justify-content-end'>
                    <IonLabel
                      onClick={() => {
                        dispatch(setIsPainRangeActivated(true))
                      }}
                      className='grid-item-divider-label'
                      color='medium'>
                      <IonNavLink className='use-range-button pain-label' color='primary'>
                        Use range
                      </IonNavLink>
                      <IonLabel className='pain-or-label'>or</IonLabel>
                    </IonLabel>

                    <IonItem lines='none' className='ion-no-padding ion-no-marging custom-unable'>
                      {painPNNVJSX}
                    </IonItem>
                  </IonCol>
                )}

                {__isPainRangeActivated && (
                  <IonCol className='d-flex justify-content-end ion-padding'>
                    <IonIcon className='delete-icon ' icon={closeCircle} onClick={(e) => handleDeletePainScale(e)} />
                  </IonCol>
                )}
              </IonRow>
            )}
            {__isPainRangeActivated && (
              <IonRow>
                <IonCol>
                  <IonItem className='ion-range-item'>
                    <IonLabel className='range-initial-label'>0</IonLabel>
                    <IonRange
                      min={0}
                      max={10}
                      snaps={true}
                      pin={true}
                      value={Number(pain)}
                      color='dark'
                      onIonChange={(e) => painOnChange(e)}
                    />

                    <IonLabel slot='end'>10</IonLabel>
                  </IonItem>
                </IonCol>
              </IonRow>
            )}
          </>
        )}

        {(!!DP_PAIN || hasPainScale || hasPainData) && (
          <IonRow className='mb-1 mt-05 ion-padding-start ion-padding-end ion-align-items-center row-gap-md'>
            {(!!DP_PAIN || hasPainScale || hasPainData) && (
              <IonCol id='painScaleButton' size='auto'>
                <ScoreButton
                  label={t('vitals.painScale.title')}
                  score={painScalesTotalsForButton}
                  isRed={true}
                  disabled={!!painPNNV}
                  onClickAction={() => history.push(TabConstants.VITALS_PAIN_SCALES)}
                />
              </IonCol>
            )}
            {(DP_PAIN_QUALITY === 1 || painQualityHasData) && (
              <IonCol size='auto' id='painQualityButton'>
                <ScoreButton
                  label={t('vitals.painQuality.title')}
                  score={null}
                  showCheckMark={painQualityHasData}
                  disabled={!!painPNNV}
                  onClickAction={handlePainQualityClick}
                />
              </IonCol>
            )}
          </IonRow>
        )}

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='StrokeScale'
              options={strokeScaleOptions}
              selectedOption={strokeScale}
              listHeader={t('vitals.advanced.form.StrokeScale')}
              label={t('vitals.advanced.form.StrokeScale')}
              action={strokeScaleOnChange}
              selectedPNNVOption={strokeScalePNNV}
              pnnvAction={strokeScalePNNVOnChange}
            />
          </IonCol>
        </IonRow>
      </IonGrid>

      {(() => {
        const shouldShow = allowedScoreIds.size > 0
        const displayStrokeScore = strokeScaleScoreTotalPNNV ? null : strokeScaleScoreTotal ?? 'N/A'
        return shouldShow ? (
          <IonGrid className='ion-no-padding'>
            <IonRow className='ml-1 mb-1 mt-05'>
              <IonCol>
                <ScoreButton
                  label={t('vitals.strokeScore.StrokeScore')}
                  score={displayStrokeScore}
                  isRed={true}
                  onClickAction={() => {
                    history.push(TabConstants.VITALS_STROKE_SCORE)
                  }}
                />
              </IonCol>
            </IonRow>
          </IonGrid>
        ) : null
      })()}

      {(DP_SEDATION > 0 || hasSedationScale) && (
        <>
          <hr className='field-divider' />
          <IonGrid className='ion-no-padding'>
            <IonRow className='ml-1 mb-05 mt-05 ion-align-items-center row-gap-md'>
              <IonCol id='sedationScaleButton' size='auto'>
                <ScoreButton
                  label={t('vitals.sedationScale.title')}
                  score={sedationScaleTotal}
                  isRed={true}
                  onClickAction={() => {
                    history.push(TabConstants.VITALS_SEDATION_SCALE)
                  }}
                />
              </IonCol>
              {(DP_WITHDRAWAL === 1 || !!hasWithdrawalScaleData) && (
                <IonCol size='auto' id='withdrawalScaleButton'>
                  <ScoreButton
                    label={t('vitals.withdrawalScale.title')}
                    score={withdrawalScaleTotal || 'N/A'}
                    isRed={true}
                    onClickAction={() => history.replace(TabConstants.VITALS_WITHDRAWAL_SCALES)}
                  />
                </IonCol>
              )}
            </IonRow>
          </IonGrid>
        </>
      )}

      {/*strokeScoreFlag && <StrokeScaleSection />*/}
      {showCustomVitals && (
        <>
          <IonItemDivider color='light' className='ion-padding'>
            <IonLabel className='grid-item-divider-label' color='medium'>
              {t('vitals.advanced.customVitals')}
            </IonLabel>
          </IonItemDivider>

          <IonGrid className='ion-no-padding'>
            {editLabelMode && (
              <IonRow className='mt-4 mb-4 ml-4'>
                <IonLabel color='medium' className='input-label'>
                  {t('vitals.advanced.customVitals1')}
                </IonLabel>
              </IonRow>
            )}

            <IonRow>
              {editLabelMode && (
                <IonCol>
                  <SInput
                    label={t('vitals.advanced.Label')}
                    dataTestID='CustomVitalLabel1'
                    inputValue={showConfirmation.label1 ? tempCustomVitalLabel.label1 : customVitalLabel1}
                    onChangeAction={setCustomVitalLabel1OnChange}
                  />
                  <Validation id={VitalsConstants.SET_CUSTOM_VITAL_LABEL1} cardinalityKey={key} />
                </IonCol>
              )}
              <IonCol className='picklist'>
                <SInput
                  label={fixedLabelMode ? fixedLabel1 : t('vitals.advanced.Value')}
                  dataTestID='CustomVitalValue1'
                  inputValue={customVitalValue1}
                  onChangeAction={setCustomVitalValue1OnChange}
                />
                <Validation id={VitalsConstants.SET_CUSTOM_VITAL_VALUE1} cardinalityKey={key} />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />

            {editLabelMode && (
              <IonRow className='mt-4 mb-4 ml-4'>
                <IonLabel color='medium' className='input-label'>
                  {t('vitals.advanced.customVitals2')}
                </IonLabel>
              </IonRow>
            )}

            <IonRow>
              {editLabelMode && (
                <IonCol>
                  <SInput
                    label={t('vitals.advanced.Label')}
                    dataTestID='CustomVitalLabel2'
                    inputValue={showConfirmation.label2 ? tempCustomVitalLabel.label2 : customVitalLabel2}
                    onChangeAction={setCustomVitalLabel2OnChange}
                  />
                  <Validation id={VitalsConstants.SET_CUSTOM_VITAL_LABEL2} cardinalityKey={key} />
                </IonCol>
              )}
              <IonCol className='picklist'>
                <SInput
                  label={fixedLabelMode ? fixedLabel2 : t('vitals.advanced.Value')}
                  dataTestID='CustomVitalValue2'
                  inputValue={customVitalValue2}
                  onChangeAction={setCustomVitalValue2OnChange}
                />
                <Validation id={VitalsConstants.SET_CUSTOM_VITAL_VALUE2} cardinalityKey={key} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </>
      )}

      <GlasgowComaScaleComponent />
      {isApgarSectionVisible() && <ApgarSection />}

      <IonItemDivider color='light' />
      <hr className='field-divider' />
      <IonRow>
        <IonCol className='picklist'>
          <SingleSelect
            dataTestID='Protocol'
            options={protocolOptions}
            selectedOption={protocol}
            listHeader={t('vitals.advanced.form.Protocol')}
            label={t('vitals.advanced.form.Protocol')}
            action={protocolOnChange}
          />
        </IonCol>
      </IonRow>
      <IonItemDivider color='light' />

      <IonRow>
        <IonCol className='picklist'>
          <IonItem lines='none'>
            <IonLabel position='stacked' className={textAreaStyles['text-area-label']}>
              {t('vitals.details.form.Comments')}
            </IonLabel>
            <div data-private={isPHI} style={getStyleForPHI(isPHI)}>
              <IonTextarea
                rows={6}
                cols={20}
                value={comments}
                color='primary-alt'
                onIonChange={(e) => {
                  commentsOnChange(e)
                }}
                maxlength={4000}
                onFocus={onFocus}
                onBlur={onBlur}
                autocapitalize='sentences'
              />
            </div>
            <div className='footer'>
              <Validation id={VitalsConstants.SET_COMMENTS} cardinalityKey={key} />
              <h1 className={comments?.length < 4000 ? 'text-color-blue-tint' : 'text-color-red'}>
                {comments?.length}/4000
              </h1>
            </div>
          </IonItem>
        </IonCol>
      </IonRow>
      {isFocused && (
        <IonItemDivider color='light' className={isTabletDevice ? 'bottom-divider-tablet' : 'bottom-divider-mobile'} />
      )}
    </IonList>
  )
}

export default AdvancedSection
